/**
 * Anonymous visitor + session identifiers used for first-party analytics.
 *
 * No personal data is stored — just random IDs. When the visitor has opted out
 * of advertising cookies (or sends a GPC signal), the visitor ID is withheld
 * and events are logged as aggregate-only rows.
 */
import { getAdConsent } from "./consent";

const VISITOR_KEY = "wg_visitor_id";
const SESSION_KEY = "wg_session_id";

const randomId = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

/** Stable per-browser ID, or null when the visitor opted out of tracking. */
export const getVisitorId = (): string | null => {
  if (typeof window === "undefined") return null;
  if (getAdConsent() === "denied") return null;
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

/** Per-tab session ID (resets when the tab is closed). */
export const getSessionId = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = randomId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};
